import { useState } from 'react'
import { Sparkles } from 'lucide-react'
import { SkerpaSuggestionCard, type Suggestion } from './SkerpaSuggestionCard'

interface Props {
  suggestions: Suggestion[]
  onApply?: (s: Suggestion) => Promise<void> | void
  onDismiss?: (s: Suggestion) => void
}

export function SkerpaSuggestionsTab({ suggestions, onApply, onDismiss }: Props) {
  const [dismissed, setDismissed] = useState<Set<string>>(new Set())
  const [applyingId, setApplyingId] = useState<string | null>(null)

  const visible = suggestions.filter(s => !dismissed.has(s.id))

  const handleApply = async (s: Suggestion) => {
    if (!onApply) return
    setApplyingId(s.id)
    try {
      await onApply(s)
      setDismissed(prev => new Set(prev).add(s.id))
    } finally {
      setApplyingId(null)
    }
  }

  const handleDismiss = (s: Suggestion) => {
    setDismissed(prev => new Set(prev).add(s.id))
    onDismiss?.(s)
  }

  if (visible.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center px-8 py-12 text-center">
        <div
          className="flex h-10 w-10 items-center justify-center rounded-full mb-3"
          style={{ backgroundColor: '#fef9e7' }}
        >
          <Sparkles className="h-5 w-5" style={{ color: '#d4a82a' }} />
        </div>
        <div className="text-[13px] font-semibold text-text mb-1">No suggestions yet</div>
        <div className="text-[12px] text-text-muted max-w-[240px] leading-relaxed">
          Skerpa surfaces ideas here as it analyses your project. Keep adding data to unlock more.
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1 overflow-y-auto px-4 py-3 flex flex-col gap-2.5">
      {visible.map(s => (
        <SkerpaSuggestionCard
          key={s.id}
          suggestion={s}
          applying={applyingId === s.id}
          onApply={handleApply}
          onDismiss={handleDismiss}
        />
      ))}
    </div>
  )
}
